<template>
  <div class="view-adminuseredit">
    <div class="breadcrump-trail">
      <router-link :to="{ name: 'admin-home' }"> Admin </router-link> /
      <router-link :to="{ name: 'admin-users-list' }"> Nutzer </router-link>
      / {{ userId }}
    </div>

    <template v-if="user">
      <h1>Nutzer Stammdaten</h1>

      <div v-if="!editingUser">
        <div class="label">{{ $t("labels.email") }}</div>
        <div>{{ user.email }}</div>

        <div class="label mt-2">{{ $t("labels.name") }}</div>
        <div>{{ user.name }}</div>

        <div class="label mt-2">{{ $t("labels.language") }}</div>
        <div class="flex gap-3 items-center">
          <div>{{ user.language }}</div> <locale-button :locale="user.language" style="pointer-events: none" />
        </div>

        <div class="label mt-2">Weiteres</div>
        <ul class="mv-0">
          <li v-if="user.isUserAdmin">
            {{
              user.isUserAdmin
                ? $t("labels.userAdmin")
                : "Kein Benutzeradministrator"
            }}
          </li>
          <li v-if="user.isSamplemodelAdmin">
            {{
              user.isSamplemodelAdmin
                ? $t("labels.sampleModelAdmin")
                : "Kein Beispielmodell-Admin"
            }}
          </li>
          <li v-if="user.isPublisher">
            {{ user.isPublisher ? "Kann veröffentlichen" : "--" }}
          </li>
          <li v-if="user.isBetaEnabled">
            {{ user.isBetaEnabled ? "Beta-Features verfügbar" : "--" }}
          </li>
          <li v-if="user.userWasDeactivated">
            {{
              user.userWasDeactivated
                ? $t("labels.deactivated")
                : "Nicht deaktiviert"
            }}
          </li>
        </ul>
      </div>
      <admin-user-form
        v-if="editingUser"
        :user="user"
        :submit-label="$t('actions.save')"
        :field-errors="fieldErrors"
        @submit="onSave"
      />
      <button
        class="button button_secondary mt-3"
        type="button"
        @click="editingUser = !editingUser"
      >
        {{ editingUser ? $t("labels.abort") : $t("labels.edit") }}
      </button>

      <div>
        <h3>{{ $t("labels.emails") }}</h3>
        <ul v-if="user?.notifiedOfUpcomingDeletion">
          <li v-if="user?.notifiedOfUpcomingDeletion">
            Löschungswarnung am
            {{ formatDateTime(user.notifiedOfUpcomingDeletion) }}
          </li>
        </ul>
        <p v-else>
          {{ $t("errors.noEntries") }}
        </p>
      </div>

      <section>
        <h3 class="mb-2">{{ $t("labels.dates") }}</h3>
        <ul class="mt-2 column gap-1">
          <li>
            <strong>Beigetreten</strong> am {{ formatDateTime(user.created) }}
          </li>
          <li>
            <strong>Letzter Login</strong> am
            {{ formatDateTime(user.lastLogin) }}
          </li>
          <li>
            <strong>Abonnement(s) gültig</strong> bis
            {{ formatDateTime(user.maxValidUntil) }}
          </li>
          <li v-if="user?.lastValidDate">
            <strong>Letzte Kontoaktivität</strong> am
            {{ formatDateTime(user?.lastValidDate) }}
          </li>
          <li>
            <strong>Zuletzt verwendete Version</strong>
            {{ user?.lastUsedAppVersion || "?" }}
          </li>
        </ul>
      </section>

      <template v-if="lexofficeContactIds && lexofficeContactIds.length > 0">
        <h3>LexOffice-Kontakte</h3>
        <ul>
          <li v-for="contactId of lexofficeContactIds" :key="contactId">
            <a :href="`https://app.lexware.de/contacts/${contactId}`" target="_blank">{{ contactId }}</a>
          </li>
        </ul>
      </template>

      <template v-if="subscriptionRequests.length > 0">
        <hr class="mt-6">
        <h2>Angebot gesendet</h2>
        <ul class="pl-0">
          <li v-for="request of subscriptionRequests" class="view-adminuseredit__pendingsub">
            <div>
              <h3 class="mt-0 mb-1">{{ $t(`subscriptions.${request.plan}.name`) }}</h3>
              <div class="color-text-medium mv-1 mb-2">
                bestellt am {{ formatDateString(request.created) }}
              </div>
              <div class="mb-2">
                <div>
                  {{ request.seats }} Nutzer
                </div>

                <div v-for="upgrade of request.upgrades" :key="upgrade" class="mt-1">
                  {{ $t(`upgrades.${upgrade}.name`) }}
                </div>

                <div class="mt-1">
                  {{ $t("labels.licenseStartDate") }}:
                  <strong>
                    <template v-if="request.startDate == null">
                      {{ $t("labels.orderDate") }}
                    </template>
                    <template v-else>
                      {{ formatDateString(request.startDate, false) }}
                    </template>
                  </strong>
                </div>

                <div class="mt-1">
                  {{ $t("labels.licenseDuration") }}: <strong>{{ $t("count.years", request.years) }}</strong>
                </div>

                <div v-if="request.renewAutomatically" class="mt-1">
                  {{ $t("texts.renewsAutomatically") }}
                </div>
              </div>
              <a
                v-if="request.lexofficeQuoteId"
                :href="`https://app.lexware.de/permalink/quotations/view/${request.lexofficeQuoteId}`"
                target="_blank"
              >
                Angebot ansehen <lucide-icon name="square-arrow-out-up-right" />
              </a>
              <div v-else class="message-warning mv-2">
                <lucide-icon name="triangle-alert" /> Keine LexOffice-Verknüpfung
              </div>
            </div>
            
            <div class="p-2">
              <div>{{ request.billingAddress.company }}</div>
              <div>{{ request.billingAddress.firstname }} {{ request.billingAddress.lastname }}</div>
              <div>{{ request.billingAddress.street }}</div>
              <div v-if="request.billingAddress.supplement">{{ request.billingAddress.supplement }}</div>
              <div>{{ request.billingAddress.zipcode }} {{ request.billingAddress.city }}</div>
              <div>{{ request.billingAddress.country }}</div>
              <div v-if="request.billingAddress.vatId">Ust.Id: {{ request.billingAddress.vatId }}</div>
            </div>

            <div class="column">
              <div v-if="request.confirmedAt != null" class="message-success p-4">
                Bestätigt am: {{ formatDateString(request.confirmedAt) }}
              </div>
              <button v-else @click="activateSubscriptionRequest(request)" type="button" class="button" :disabled="activatingSubscriptionRequest">
                Angebot bestätigen
              </button>
              <button @click="deleteSubscriptionRequest(request)" type="button" class="button button--danger button_secondary">
                Angebot zurückziehen
              </button>
            </div>
          </li>
        </ul>
      </template>

      <hr class="mt-6" />

      <div class="flex gap-4">
        <router-link
          :to="{
            name: 'admin-subscription-create',
            params: { userId: userId }
          }"
          class="button mb-4"
        >
          Abo für den Nutzer anlegen
        </router-link>

        <router-link
          :to="{
            name: 'admin-subscription-request-create',
            params: { userId: userId }
          }"
          class="button button_secondary mb-4"
        >
          Angebot anlegen und senden
        </router-link>
      </div>

      <h2>{{ $t("objectNames.subscription", 4) }}</h2>

      <!-- SUBSCRIPTIONS -->
      <table
        v-if="subscriptions.length > 0"
        class="view-adminuseredit__licensehistory"
      >
        <thead>
          <tr>
            <th>Plan</th>
            <th>Erstelldatum</th>
            <th>Gültig Bis</th>
            <th>Inhalt</th>
            <th>Kontaktdaten</th>
            <th>Rechnungen</th>
            <th>Empfangene Mails</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="subscription in subscriptions" :key="subscription.id">
            <td>
              <strong>{{ subscription?.planInfo?.label }}</strong>
              <div v-if="subscription?.organization">
                <router-link :to="{
                  name: 'admin-organizations-detail',
                  params: { organizationId: subscription?.organization.id }
                }">
                  {{ subscription?.organization.name }}
                </router-link>
              </div>
            </td>
            <td>
              {{ formatDateString(subscription.created, true) }}<br />
              {{ subscription.wasAutomaticallyCreated ? "(Auto-Erneuert)" : null }}
            </td>
            <td>
              {{ formatDateString(subscription.validUntil, false) }}
              <div>
                Auto-Verlängerung:
                {{
                  subscription.renewAutomatically ? $t(`periods.${subscription.paymentPeriod}`) : $t("labels.off")
                }}
              </div>
            </td>
            <td>
              <div class="column">
                <div>{{ subscription.seats }} Nutzer</div>
                <div v-for="upgrade in subscription.upgrades" class="mv-2">
                  {{ $t(`upgrades.${upgrade}.name`) }}
                </div>
              </div>
            </td>
            <td>
              {{ subscription?.billingAddress?.firstname }}
              {{ subscription?.billingAddress?.lastname }}<br />
              {{ subscription?.billingAddress?.company }}<br />
              {{ subscription?.billingAddress?.street }}<br />
              {{ subscription?.billingAddress?.supplement }}<br />
              {{ subscription?.billingAddress?.zipcode }}
              {{ subscription?.billingAddress?.city }}<br />
              {{ subscription?.billingAddress?.country }}<br>
              <br/>
              Ust.Id: {{ subscription?.billingAddress?.vatId }}

              <div v-if="subscription.lexofficeContactId" class="mt-4">
                <a
                  :href="`https://app.lexware.de/contacts/${subscription.lexofficeContactId}`"
                  target="_blank"
                >
                  LexOffice-Kontakt <lucide-icon name="square-arrow-out-up-right" />
                </a>
              </div>
              <div v-if="subscription.stripeCustomerId" class="mt-2">
                <a
                  :href="`https://dashboard.stripe.com/customers/${subscription.stripeCustomerId}`"
                  target="_blank"
                >
                  Stripe-Kunde <lucide-icon name="square-arrow-out-up-right" />
                </a>
              </div>
              <div v-if="subscription.stripeSubscriptionId" class="mt-2">
                <a
                  :href="`https://dashboard.stripe.com/subscriptions/${subscription.stripeSubscriptionId}`"
                  target="_blank"
                >
                  Stripe-Abonnement <lucide-icon name="square-arrow-out-up-right" />
                </a>
              </div>
            </td>
            <td>
              <div v-for="invoice in subscription.invoices" :key="invoice.stripeInvoiceId" class="column mt-2">
                <h5 class="mb-1">Rechnung vom {{ formatDateString(invoice.created) }}</h5>
                <a
                  v-if="invoice.stripeInvoiceId"
                  :href="`https://dashboard.stripe.com/invoices/${invoice.stripeInvoiceId}`"
                  target="_blank"
                >
                  Bei Stripe <lucide-icon name="square-arrow-out-up-right" />
                </a>
                <a
                  v-if="invoice.lexofficeInvoiceId"
                  :href="`https://app.lexware.de/permalink/invoices/view/${invoice.lexofficeInvoiceId}`"
                  target="_blank"
                >
                  Bei LexOffice <lucide-icon name="square-arrow-out-up-right" />
                </a>
              </div>
            </td>
            <td>
              <div>
                {{
                  subscription.notifiedOfUpcomingExpiration
                    ? $t("labels.accountWillExpireEmail")
                    : "-"
                }}
              </div>
              <div>
                {{
                  subscription.notifiedOfUpcomingRenewal
                    ? $t("labels.receivedRenewalEmail")
                    : "-"
                }}
              </div>
            </td>
            <td class="text-center">
              <span v-if="subscription.expired" class="text-danger" :title="`Abgelaufen am ${subscription.expiredAt}`">🛑</span>
              <span v-else title="Aktiv">🟢</span>
            </td>
            <td>

              <router-link class="button-icon mi-auto mv-4" :to="{ name: 'admin-user-update-subscription', params: { userId: userId, subscriptionId: subscription.id } }">
                <lucide-icon name="pencil" />
              </router-link>

              <button type="button" class="button-icon mi-auto mt-4" @click="deleteSubscription(subscription)">
                <lucide-icon name="trash-2" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else>Dieser Nutzer besitzt keine Lizenzen oder Organisationen.</p>

      <hr class="mt-7" />

      <h2>{{ $t("headings.organizations") }}</h2>

      <table
        v-if="organizationLicenses.length > 0"
        class="view-adminuseredit__licensehistory"
      >
        <thead>
          <tr>
            <th>Organisation</th>
            <th>Typ</th>
            <th>Nutzer bis</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="organizationLicense in organizationLicenses"
            :key="organizationLicense.id"
          >
            <td>
              <router-link
                :to="{
                  name: 'admin-organizations-detail',
                  params: { organizationId: organizationLicense.organizationId }
                }"
              >
                {{ organizationLicense?.organizationName }} ({{
                  organizationLicense?.organizationOwner?.email
                }})
              </router-link>
            </td>
            <td>
              {{ organizationLicense?.roleLabel }}
            </td>
            <td>
              {{
                organizationLicense?.licenseValidUntil
                  ? formatDateString(organizationLicense?.licenseValidUntil)
                  : "Organization-Auflösung"
              }}
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else>Der Nutzer ist Mitglied in keiner Organization.</p>

      <hr class="mv-7" />

      <article
        v-if="(user?.orderHistory?.length || 0) > 0"
        class="order-history"
      >
        <h2>{{ $t("headings.orderHistory") }}</h2>
        <table class="order-history__items">
          <thead>
            <tr class="order-history__header">
              <th>{{ $t("labels.orderDate") }}</th>
              <th>{{ $t("labels.type") }}</th>
              <th>{{ $t("labels.price") }}</th>
              <th>{{ $t("labels.article") }}</th>
              <th>{{ $t("words.user", 2) }}</th>
              <th>{{ $t("labels.billingAddress") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in user.orderHistory"
              :key="item.created"
              class="order-history__item"
            >
              <td>{{ formatDateTime(item.created) }}</td>
              <td>{{ $t(`ordertypes.${item.type}`) }}</td>
              <td>{{ item.price }} {{ item.priceCurrency }}</td>
              <td>{{ plans?.[item.itemType]?.label ?? item.itemType }}</td>
              <td>
                <div>{{ $t("labels.countUsers", item.licenses) }}</div>
                <div>{{ $t("labels.xTeams", item.teams) }}</div>
              </td>
              <td>
                <small>
                  <div>
                    {{ item?.billingAddress?.firstname }}
                    {{ item?.billingAddress?.lastname }}
                  </div>
                  <div>{{ item?.billingAddress?.company }}</div>
                  <div>
                    {{ item?.billingAddress?.street }}
                  </div>
                  <div v-if="item?.billingAddress?.supplement">
                    {{ item?.billingAddress?.supplement }}
                  </div>
                  <div>
                    {{ item?.billingAddress?.zipcode }}
                    {{ item?.billingAddress?.city }}
                  </div>
                  <div>{{ item?.billingAddress?.country }}</div>
                  
                  <div v-if="item?.billingAddress?.vatId" class="mt-1">{{$t("labels.vatId")}}{{$t(":")}} {{ item?.billingAddress?.vatId }}</div>
                </small>
              </td>
            </tr>
          </tbody>
        </table>
      </article>
      <p v-else class="message message-info">
        Keine aufgezeichneten Bestellungen.
      </p>

      <hr class="mt-7" />

      <button
        class="button button_secondary"
        type="button"
        @click="tryDeletingUser()"
      >
        {{ $t("actions.deleteUser") }}
      </button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { uniq } from "lodash-es";
import { formatDate } from "@/i18n";
import { showAlert, showConfirm, showError, showMessage } from "@core/ui/alert/alert";
import { useAdmin } from "./useAdmin";
import { useToast } from "vue-toastification";

import AdminUserForm from "./AdminUserForm.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import LocaleButton from "@core/input/LocaleButton.vue";

import { usePlans } from "@manager/plans/plans.store";
import type { AdminUser } from "@/api/User";
import { BadRequestError } from "@core/errors/Errors";
import { notNull } from "@/helpers/utility/Type";

const props = defineProps<{
  userId?: string;
}>();

const { t } = useI18n();
const router = useRouter();
const adminAPI = useAdmin();
const toast = useToast();

const { plans } = usePlans();

const user = ref<AdminUser | null>(null);

const editingUser = ref(false);

const fieldErrors = ref<Record<string, string | null>>({
  email: null as null | string,
  firstname: null as null | string,
  lastname: null as null | string,
  company: null as null | string,
  street: null as null | string,
  number: null as null | string,
  zipcode: null as null | string,
  city: null as null | string,
  country: null as null | string
});

const subscriptions = computed(() => {
  return (user.value?.subscriptions ?? []).sort((a, b) => {
    return a.created < b.created ? 1 : -1;
  });
});
const organizationLicenses = computed(() => {
  return user.value?.organizationLicenses ?? [];
});

const subscriptionRequests = computed(() => {
  return user.value?.subscriptionRequests ?? [];
});

const lexofficeContactIds = computed(() => {
  return uniq(user.value?.subscriptions?.map((s) => s.lexofficeContactId).filter(notNull)) ?? [];
});

const activatingSubscriptionRequest = ref(false);

async function activateSubscriptionRequest(subscriptionRequest: { id: number; plan: string }) {
  const alert = await showAlert<{
    generateInvoice: boolean;
  }>({
    title: "Angebot bestätigen",
    confirmText: "Angebot bestätigen",
    denyText: "Abbrechen",
    content: [
      { 
        type: "text",
        text: "Möchtest du, dass automatisch eine Rechnung erstellt und versendet wird? Dabei werden genau die Angaben des Angebots übernommen."
      },
      {
        type: "checkbox",
        name: "generateInvoice",
        label: "Automatisch Rechnung erstellen und versenden",
        value: false,
        class: "mb-2 mt-2"
      },
      {
        type: "text",
        text: "Bitte nur auswählen, wenn keine Rechnung manuell versandt wird.",
        class: "message-warning p-4 mb-4"
      }
    ]
  });
  if (!alert) return;
  try {
    activatingSubscriptionRequest.value = true;
    type SubscriptionRequest = NonNullable<AdminUser["subscriptionRequests"]>[number];
    type Subscription = NonNullable<AdminUser["subscriptions"]>[number];
    const responseObject = await adminAPI.post<SubscriptionRequest | Subscription>(
      `/users/${props.userId}/subscription-requests/${subscriptionRequest.id}/activate`,
      {
        data: {
          generateInvoice: alert.generateInvoice,
        }
      }
    );
    if ("startDate" in responseObject) {
      // Response is a subscription request, update it:
      const request = responseObject;
      const index = subscriptionRequests.value.findIndex((s) => s.id === subscriptionRequest.id);
      subscriptionRequests.value.splice(index, 1, request);
    } else {
      // Response is a subscription, add it to the list:
      const createdSubscription = responseObject;
      const index = subscriptionRequests.value.findIndex((s) => s.id === subscriptionRequest.id);
      subscriptionRequests.value.splice(index, 1);
      const subIndex = subscriptions.value.findIndex((s) => s.id === createdSubscription.id);
      subscriptions.value.splice(subIndex, 1, createdSubscription);
    }

    toast.success("Angebot bestätigt");
  } catch (error) {
    showError(error as Error);
  } finally {
    activatingSubscriptionRequest.value = false;
  }
}
async function deleteSubscriptionRequest(subscriptionRequest: { id: number }) {
  try {
    const questionText = "Sicher, dass du das Angebot zurückziehen möchtest?";
    const confirmText = "Angebot zurückziehen";
    const deleteConfirmed = await showConfirm(
      questionText, confirmText
    );
    if (!deleteConfirmed) return;
    await adminAPI.destroy(`/users/${props.userId}/subscription-requests/${subscriptionRequest.id}`);
    const index = subscriptionRequests.value.findIndex((s) => s.id === subscriptionRequest.id);
    subscriptionRequests.value.splice(index, 1);
  } catch (error) {
    showError(error as Error);
  }
}

async function deleteSubscription(subscription: { id: number; plan: string }) {
  try {
    const questionText = "Sicher, dass du das Abo löschen möchtest?";
    const confirmText = "Abo löschen";
    const deleteConfirmed = await showConfirm(
      questionText, confirmText
    );
    if (!deleteConfirmed) return;
    await adminAPI.destroy(`/users/${props.userId}/subscriptions/${subscription.id}`);
    const index = subscriptions.value.findIndex((s) => s.id === subscription.id);
    subscriptions.value.splice(index, 1);
  } catch (error) {
    showError(error as Error);
  }
}

async function loadUser() {
  try {
    const loadedUser = await adminAPI.get<AdminUser>(`/users/${props.userId}`);
    user.value = loadedUser;
  } catch (error) {
    await showError(error as Error);

    router.push({
      name: "admin-users-list"
    });
  }
}
loadUser();

async function onSave(userData: Partial<AdminUser>) {
  try {
    const savedUser = await adminAPI.patch<AdminUser>(`/users/${props.userId}`, {
      data: userData
    });
    user.value = savedUser;
  } catch (error) {
    if (error instanceof BadRequestError && error.fields) {
      fieldErrors.value = error.fields;
    } else {
      showError(error as Error);
    }
  }
}

function formatDateString(dateString: string | null, withTime = true) {
  if (dateString == null) return "—";
  const d = new Date(dateString);
  if (withTime) return formatDate(d, "f");
  return formatDate(d, "D");
}

async function tryDeletingUser() {
  try {
    if (await showConfirm(t("questions.confirmDeleteUser"))) {
      await adminAPI.destroy(`/users/${props.userId}`);
      await showMessage(t("messages.deletedSuccessfully"));

      router.push({
        name: "admin-users-list"
      });
    }
  } catch (error) {
    console.log(error);
    showError(error as Error);
  }
}

function formatDateTime(datetimeString?: string) {
  if (!datetimeString) return "";
  const date = new Date(datetimeString);
  return formatDate(date, "f");
}
</script>

<style lang="scss">
.view-adminuseredit {
  padding: 2rem;
  padding-bottom: 6rem;
  max-width: 90rem;
  width: 100%;

  &__licensehistory {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
    th,
    td {
      padding: 0.5rem 1rem;
      border: 1px solid var(--color-border);
    }
  }

  &__pendingsub {
    list-style: none;
    background-color: var(--color-background-close);
    padding: 1rem;
    box-shadow: 0 1px 3px rgba(var(--tint-contrast), 0.2);
    display: grid;
    gap: 1rem;
    grid-template-columns: 1fr max-content max-content;
  }
}
</style>
